import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

// Persistent token storage: localStorage + cookie fallback for PWA resilience
function saveToken(token) {
  try { localStorage.setItem('token', token); } catch (e) {}
  document.cookie = `vf_token=${encodeURIComponent(token)};path=/;max-age=${60*60*24*90};SameSite=Lax`;
}

function saveUser(userData) {
  try { localStorage.setItem('vf_user', JSON.stringify(userData)); } catch (e) {}
}

function getSavedUser() {
  try {
    const u = localStorage.getItem('vf_user');
    if (u) return JSON.parse(u);
  } catch (e) {}
  return null;
}

function getToken() {
  try {
    const t = localStorage.getItem('token');
    if (t) return t;
  } catch (e) {}
  // Fallback: read from cookie
  const match = document.cookie.match(/(?:^|;\s*)vf_token=([^;]*)/);
  if (match) {
    const t = decodeURIComponent(match[1]);
    try { localStorage.setItem('token', t); } catch (e) {} // Re-sync
    return t;
  }
  return null;
}

function clearToken() {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('vf_user');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('adminEmail');
  } catch (e) {}
  document.cookie = 'vf_token=;path=/;max-age=0';
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getSavedUser());
  const [token, setToken] = useState(getToken());
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Skip fetching user for admin (admin uses different auth)
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (isAdmin && token) {
      const adminEmail = localStorage.getItem('adminEmail') || 'admin';
      setUser({ user_type: 'admin', email: adminEmail });
      setLoading(false);
      return;
    }
    
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    // Don't fetch for admin
    if (localStorage.getItem('isAdmin') === 'true') {
      setLoading(false);
      return;
    }
    
    const currentToken = getToken();
    if (!currentToken) {
      setLoading(false);
      return;
    }
    
    // Use XMLHttpRequest to avoid "body stream already read" issue
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API_URL}/api/auth/me`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${currentToken}`);
      
      xhr.onload = function() {
        try {
          if (xhr.status >= 200 && xhr.status < 300) {
            const data = JSON.parse(xhr.responseText);
            setUser(data);
            saveUser(data);
          } else if (xhr.status === 401) {
            // Only clear token on explicit 401 (unauthorized)
            clearToken();
            setToken(null);
            setUser(null);
          }
          // For other errors (500, network issues), don't clear the token
          // The user might just be experiencing temporary connectivity issues
        } catch (e) {
          console.error('Auth parse error:', e);
        }
        setLoading(false);
        resolve();
      };
      
      xhr.onerror = function() {
        console.error('Auth network error');
        // Don't clear token on network error - might be temporary
        // User data from localStorage will keep them "logged in"
        setLoading(false);
        resolve();
      };
      
      xhr.send();
    });
  };

  const login = async (email, password) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          
          if (xhr.status >= 200 && xhr.status < 300) {
            saveToken(data.token);
            setToken(data.token);
            setUser(data.user);
            saveUser(data.user);
            resolve(data);
          } else {
            // Provide user-friendly error messages
            if (data.detail === 'Invalid credentials') {
              reject(new Error('Incorrect email or password. Please try again.'));
            } else {
              reject(new Error(data.detail || 'Login failed'));
            }
          }
        } catch (e) {
          reject(new Error('Server error. Please try again.'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Unable to connect to server. Please check your internet connection.'));
      };
      
      xhr.send(JSON.stringify({ email, password }));
    });
  };

  const register = async (userData) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/register`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          
          if (xhr.status >= 200 && xhr.status < 300) {
            saveToken(data.token);
            setToken(data.token);
            setUser(data.user);
            saveUser(data.user);
            resolve(data);
          } else {
            // FastAPI 422 errors return detail as an array of {loc,msg,type} objects.
            // Coerce to a readable string so React never renders "[object Object]".
            let msg = 'Registration failed';
            if (typeof data.detail === 'string') {
              msg = data.detail;
            } else if (Array.isArray(data.detail) && data.detail.length > 0) {
              msg = data.detail.map(e => {
                const field = Array.isArray(e.loc) ? e.loc[e.loc.length - 1] : '';
                return field ? `${field}: ${e.msg}` : e.msg;
              }).join('; ');
            } else if (data.detail && typeof data.detail === 'object') {
              msg = data.detail.msg || JSON.stringify(data.detail);
            }
            reject(new Error(msg));
          }
        } catch (e) {
          reject(new Error('Server error. Please try again.'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Unable to connect to server. Please check your internet connection.'));
      };
      
      xhr.send(JSON.stringify(userData));
    });
  };

  const logout = () => {
    clearToken();
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  const adminLogin = async (email, password) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/admin/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          
          if (xhr.status >= 200 && xhr.status < 300) {
            saveToken(data.token);
            localStorage.setItem('isAdmin', 'true');
            localStorage.setItem('adminEmail', email);
            setToken(data.token);
            const adminUser = { user_type: 'admin', email };
            setUser(adminUser);
            saveUser(adminUser);
            setLoading(false);
            resolve(data);
          } else {
            reject(new Error(data.detail || 'Admin login failed'));
          }
        } catch (e) {
          reject(new Error('Server error. Please try again.'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Unable to connect to server. Please check your internet connection.'));
      };
      
      xhr.send(JSON.stringify({ email, password }));
    });
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      refreshUser,
      adminLogin,
      isAuthenticated: !!user,
      // "Subscribed" for routing purposes = has any active-billing relationship.
      // past_due and suspended users have a real subscription that just needs a
      // card update — bounce them to the dashboard so they see the banner instead
      // of the "pick a plan" checkout flow.
      isSubscribed: ['active', 'canceling', 'past_due', 'suspended'].includes(user?.subscription_status)
    }}>
      {children}
    </AuthContext.Provider>
  );
};
